import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

export const angles = ['21', '28', '27', '22', '23'] as const

/**
 * Finds the primary product image URL of a given product image set (angle 21).
 * IMPORTANT: Always use this function as the default choice for product images
 * unless specific angles or variation attributes need to be considered.
 *
 * @param {ShopperProductsTypes.Image[]} images - Array of product images from a ShopperProduct
 * @returns {string} URL of the primary image or empty string if not found (This is the DIS URL to allow for image transformation)
 *
 * @default Returns empty string if no primary angle image is found
 */
export const findPrimaryAngle = (images: ShopperProductsTypes.Image[]) => {
  const suffix = '-21.'
  const imageResult = images?.find((image) => {
    const imageName = image.disBaseLink?.substring(image.disBaseLink?.lastIndexOf('/') + 1)
    return imageName?.includes(suffix)
  })
  return imageResult?.disBaseLink || ''
}

export const findImage = (
  images: ShopperProductsTypes.Image[],
  variationValues:
    | {
        [key: string]: string
      }
    | undefined,
  angle: string,
) => {
  if (!variationValues) return undefined

  const values = Object.values(variationValues)
  for (let i = 0; i < values.length; i += 1) {
    const variantValue = values[i]
    const variantImage = images?.find((image) => {
      if (!image.disBaseLink) return

      return (
        image.disBaseLink.endsWith(`-${variantValue}-${angle}.jpg`) ||
        image.disBaseLink?.endsWith(`-${variantValue}-${angle}.png`)
      )
    })

    if (variantImage) {
      return { disBaseLink: variantImage.disBaseLink, alt: variantImage.alt, link: variantImage.link }
    }
  }

  return
}

/**
 * Finds the primary product image (angle 21) of a given product image group.
 * This function is used when a product is fetched at the parent level and you want to
 * find the primary image for a specific variation of that product.
 *
 * @param {ShopperProductsTypes.ImageGroup[]} imageGroups - The product image group array returned from a parent shopperProduct call
 * @param {{ [key: string]: string }} variationValues - Key-value pairs of variation attributes and their values
 * @returns { { disBaseLink: string, alt: string, link: string } | undefined } the primary image details or undefined if not found (This is the DIS URL to allow for image transformation)
 *
 * @default Returns undefined if no primary angle image is found which matches any of the variation values
 */
export const findPrimaryVariationImageFromGroup = (
  imageGroups: ShopperProductsTypes.ImageGroup[] | undefined,
  variationValues: { [key: string]: string } | undefined,
) => {
  if (!imageGroups) return

  for (const group of imageGroups) {
    let image
    if (variationValues) {
      image = findImage(group.images, variationValues, '21')
      if (image) return image
    } else {
      image = findPrimaryAngle(group.images ?? [])
      if (image) return { disBaseLink: image }
    }
  }
}

/**
 * Generates a regular expression to match image filenames based on the given variant value.
 * The regular expression matches filenames that contain the variant value followed by a dash,
 * and then a number between 21 and 30, followed by a period.
 *
 * @param {string} variantValue - The variant value to be included in the image filename.
 * @returns {RegExp} A regular expression to match the desired image filenames.
 */
const getImageFilter = (variantValue: string) => {
  return new RegExp(`-${variantValue}-(2[1-9]|30)\\.`)
}

const findImageNoAngles = (
  image: ShopperProductsTypes.Image,
  variationValues: string[],
): ShopperProductsTypes.Image | null => {
  for (let i = 0; i < variationValues?.length; i += 1) {
    const variantValue = variationValues[i]
    if (!variantValue) continue
    const filter = getImageFilter(variantValue)
    const variantImage = image?.disBaseLink?.match(filter) ? image : null
    if (variantImage) {
      return variantImage
    }
  }
  return null
}

/**
 * Finds and returns an array of images based on the provided variation values.
 * If no images are found or the variation values are not provided, a default image array is returned.
 *
 * @param {Image[]} images - An array of Image objects to search through.
 * @param {{ [key: string]: string }} variationValues - An object containing key-value pairs of variation values.
 * @returns {Image[]} An array of Image objects that match the variation values, or a default image array if no matches are found.
 */
export const findImageArray = (
  images: ShopperProductsTypes.Image[] | undefined,
  variationValues: { [key: string]: string } | undefined,
): ShopperProductsTypes.Image[] => {
  const i18n = useNuxtApp().$i18n
  const noImages = [
    {
      disBaseLink: '/images/product-unavailable.jpg',
      link: '/images/product-unavailable.jpg',
      title: i18n.t('product.images.unavailable_alt_text').toString(),
      alt: i18n.t('product.images.unavailable_alt_text').toString(),
    },
  ]

  if (!images?.length || !variationValues) {
    return noImages
  }

  // Do not use PIMSizeLength value to find images (#183654)
  const variations = Object.entries(variationValues)
    .filter(([key]) => key !== 'PIMSizeLength')
    .map(([, value]) => value)
  const variantImages = images
    .map((image) => {
      return findImageNoAngles(image, variations)
    })
    .filter((image) => image !== null)
  return !variantImages.length ? noImages : variantImages
}

// Masters are 1600px wide, so larger widths only upscale.
const DIS_WIDTHS = [300, 400, 500, 600, 800, 1000, 1200, 1600]
// From q90 DIS re-encodes at 4:4:4 chroma; most masters are 4:2:0, so that adds ~30% bytes and no detail.
const DIS_QUALITY = 89

const disUrl = (disBaseLink: string | undefined, width: number) => `${disBaseLink}?sw=${width}&sm=fit&q=${DIS_QUALITY}`

// Phones past 2x show little extra detail for a lot more bytes, so at 2.5x and up the slot shrinks by a third,
// which asks a 3x phone for 2x.
const PHONE_DENSITY_CAP = '(max-width: 767px) and (min-resolution: 2.5dppx)'

const layoutSizes = (phone: string, wider?: string) =>
  [`${PHONE_DENSITY_CAP} calc(${phone} * 2 / 3)`, wider, phone].filter(Boolean).join(', ')

/**
 * `sizes` for each layout product images render in: the widest the slot gets at each breakpoint, measured
 * against the CSS. Overshooting only costs bytes; undershooting serves a soft image.
 */
export const productImageSizes = {
  /** Any tile two or more abreast. */
  tile: layoutSizes('50vw', '(min-width: 1280px) 25vw'),
  /** ProductList columns (see its column control). */
  grid: layoutSizes('50vw', '(min-width: 768px) 25vw'),
  gridLess: layoutSizes('100vw', '(min-width: 768px) 50vw'),
  gridMore: layoutSizes('50vw', '(min-width: 768px) 20vw'),
  /** Swiper carousels: 9.5rem slides on mobile, four per view inside the page container from desktop. */
  carousel: layoutSizes('152px', '(min-width: 1280px) 309px, (min-width: 768px) 25vw'),
  /** Column-panel carousels stretching fewer than three products across the panel. */
  stretchedCarousel: layoutSizes('152px', '(min-width: 768px) 50vw'),
  /** Carousels that keep 9.5rem slides at every width. */
  fixedCarousel: layoutSizes('152px'),
  /** Header search panel: two per row in a 28rem panel, three in 500px from desktop. */
  search: layoutSizes('224px'),
  /** Recommendation grid: two columns, four inside the page container from desktop. */
  recommendationGrid: layoutSizes('50vw', '(min-width: 1280px) 309px'),
  /** PDP gallery: full-width swiper on mobile, one column beside the details on tablet, two-up on desktop. */
  gallery: layoutSizes('calc(100vw - 47px)', '(min-width: 1280px) 402px, (min-width: 768px) 62vw'),
  /** PDP gallery with fewer than three items, which stays one column (up to 51rem) on desktop. */
  galleryOneColumn: layoutSizes('calc(100vw - 47px)', '(min-width: 1280px) 816px, (min-width: 768px) 62vw'),
  /** Quickview: the default modal drawer, full width up to 25.875rem. */
  drawer: layoutSizes('414px'),
}

export const generateImageSrcSet = (images: ShopperProductsTypes.Image[], sizes: string = productImageSizes.tile) => {
  return images.map((image) => ({
    src: disUrl(image.disBaseLink, 600),
    srcset: DIS_WIDTHS.map((width) => `${disUrl(image.disBaseLink, width)} ${width}w`).join(', '),
    sizes,
    alt: image.alt,
  }))
}

// Zoom reaches 2x, more than any re-encode can add, so serve the master untouched.
export const generateHighResImageSrcSet = (images: ShopperProductsTypes.Image[]) => {
  return images.map((image) => ({
    src: image.disBaseLink,
    alt: image.alt,
  }))
}
